import { immerable } from 'immer';
// my imports:
import * as Util from '@/app/lib/system/util';
import LetterModule, { LMType } from '@/app/lib/game-state/letter-module';

export default class WordModule {
    [immerable] = true

    puzzleWord: string;
    index: number = -1;
    length: number;
    letters: LetterModule[];
    solved: boolean = false;
    active: boolean = true;

    constructor(formattedPuzzleWord: string, index?: number) {
        this.length = formattedPuzzleWord.length;
        this.letters = new Array(this.length);
        this.wipe();

        // build givens from capital letter inputs
        // if we build a word of all givens, mark solved
        let isAllGivens: boolean = true;
        for (let i = 0; i < formattedPuzzleWord.length; i++) {
            const char = formattedPuzzleWord[i];
            if (Util.isUppercaseLetter(char)) {
                this.makeGiven(i, char);
            } else {
                isAllGivens = false;
            }
        }
        if (isAllGivens) {
            this.solved = true;
        }
        if (index) {
            this.index = index;
        }

        this.puzzleWord = formattedPuzzleWord.toLowerCase();
    }

    import(word: string) {
        for (let i = 0; i < word.length; i++) {
            const char = word[i];
            if (Util.isUppercaseLetter(char)) {
                this.makeGiven(i, char);
            }
            else if (Util.isAlphaChar(char)){
                this.setEntry(i, char);
            }
            else {
                this.setEntry(i, '');
            }
        }
    }

    setIndex(index: number) {
        if (index < 0) {
            console.warn("Tried to set a WordModule's index to a negative number");
            return;
        }
        this.index = index;
    }

    wipe() {
        for (let i = 0; i < this.length; i++) {
            this.letters[i] = new LetterModule(i);
        }
    }

    getLettersByType(type: LMType) : LetterModule[] {
        const letters = new Array<LetterModule>();
        for (const letterModule of this.letters) {
            if (letterModule.type === type) {
                letters.push(letterModule)
            }
        }
        return letters;
    }

    makeGiven(position: number, character: string = ''): WordModule {
        if (position < 0 || position > this.length - 1) {
            console.warn("Tried to set a given in a WordModule that is out of bounds.")
            return this;
        }
        if (character === '') {
            character = this.puzzleWord[position];
        }
        if (!Util.isAlphaChar(character[0])) {
            console.warn("Tried to set a given in a WordModule to a non-alphabetic character")
            return this;
        }
        
        this.letters[position] = new LetterModule(position, character, 'given');
        return this;
    }

    makeRandomGiven() {
        // first get the entries
        const entries: LetterModule[] = this.getLettersByType('entry');
        // get a random one
        const letterModule: LetterModule = Util.getRandom(entries);
        // get the data for making a given
        const position: number = letterModule.position;
        // make the given
        this.makeGiven(position);

        return;
    }
    
    // enforces a minimum amount of givens, but privileges converting wildcards to givens if they exist
    enforceMinimumGivens(min: number, wildcards: string[] = [], biasForWildcards: number = .6) {
        // cleanup
        min = Util.stochasticInt(min);
        if (min > this.length) {
            min = this.length;
        }
        biasForWildcards = Util.clamp01(biasForWildcards);

        // check if we have wildcards in the puzzleWord
        // we only want the position if it's not already entry slot
        let entryPositionsWithWildcards: number[] = [];
        for (let i = 0; i < this.puzzleWord.length; i++) {
            if (
                wildcards.includes(this.puzzleWord[i].toLowerCase())
                && this.isEntry(i)
            ) {
                entryPositionsWithWildcards.push(i);
            }
        }

        // shuffle the positions so they're accessed randomly
        entryPositionsWithWildcards = Util.shuffle(entryPositionsWithWildcards);

        let givens: LetterModule[] = this.getLettersByType('given');
        while (givens.length < min) {
            // if we have any entries with wild card, see if we deal with those first stochastically
            if (
                entryPositionsWithWildcards.length > 0
                && Math.random() <= biasForWildcards
            ) {
                // shift removes the first element (mutates array) and returns it
                const position = entryPositionsWithWildcards.shift()!;
                this.makeGiven(position, this.puzzleWord[position]);
            } else {
                this.makeRandomGiven();
            }
            givens = this.getLettersByType('given');
        }
    }

    isGiven(letterPosition: number) : boolean {
        if (this.letters[letterPosition].type === 'given') {
            return true;
        }
        return false;
    }

    isEntry(letterPosition: number) : boolean {
        if (this.letters[letterPosition].type === 'entry') {
            return true;
        }
        return false;
    }

    wipeGivens() {
        for (const letterModule of this.letters) {
            if (letterModule.type === 'given') {
                letterModule.wipe();
            }
        }
        return;
    }

    setEntry(position: number, character: string) {
        if (position < 0 || position >= this.length) {
            console.warn("Tried to set an entry in a WordModule that is out of bounds.")
            return;
        }

        // if it's not an alphabetic character OR blank
        if (!(character === '' || Util.isAlphaChar(character[0]))) {
            console.warn("Tried to set an entry in a WordModule that is neither empty nor alphabetic")
            return;
        }
       
        // check that we're not trying to override a given
        if (this.letters[position].type === 'given') {
            console.warn("Tried to override a given in a word module")
            return;
        }
        this.letters[position] = new LetterModule(position, Util.firstChar(character).toUpperCase());
        return;
    }

    isComplete(): boolean {
        let count: number = 0;
        for (const letter of this.letters) {
            if (letter.character) {
                count++;
            }
        }
        return count >= this.length;
    }

    wipeEntries() {
        for (const letterModule of this.letters) {
            if (letterModule.type === 'entry') {
                letterModule.wipe();
            }
        }
        return;
    }

    blockSimpleNav() : boolean {
        // currently not blocking anything
        return false;

        // down here was the old logic
        if (this.solved)
            return false;

        if (!this.active)
            return true;

        return false;
    }

    blockComplexNav() : boolean {
        return (!this.active || this.solved);
    }

    // reveals the puzzleWord and marks solved
    // allows gameState to pass in a different puzzleWord, in case we've already used this one
    // unless already solved, in which case leave as is in case it's an alternate
    // that is, the player may have put in a correct word we want to leave there
    reveal(word: string = this.puzzleWord) {
        if (this.solved)
            return;

        if (word.length !== this.length)
            return;

        for (let i = 0; i < word.length; i++) {
            if(this.letters[i].type === 'entry') {
                this.setEntry(i, word[i]);
            }
        }
        this.solved = true;
    }

    // checks if a word fits into the module
    // is checking givens only, not dictionaries (bogus words will count)
    // you can pass ingredients to see if it's trying to use them where there are no givens
    validate(word: string, ingredients: string[] = []) : boolean {
        // first check length
        if (word.length !== this.length) {
            return false;
        }
        const givens: LetterModule[] = this.getLettersByType('given');
        for (const letterModule of givens) {
            // if we find a given that doesn't match
            if (word[letterModule.position].toLowerCase() !== letterModule.character) {
                return false;
            }
        }
        // if we passed in ingredients, check entry modules
        if (ingredients.length > 0) {
            const entries: LetterModule[] = this.getLettersByType('entry');
            for (const letterModule of entries) {
                // if the word passed in has a non-ingredient in an entry position it's bad
                if (!ingredients.includes(word[letterModule.position].toLowerCase())) {
                    return false;
                }
            }
        }
        return true;
    }

    isSolvableWithIngredients(ingredients: string[]) : boolean {
        // stringify complete gives us the formatted string
        const formattedWord: string = this.stringifyComplete();
        for (let i = 0; i < formattedWord.length; i++) {
            if (this.letters[i].type === 'given') {
                continue;
            }
            if (!ingredients.includes(formattedWord[i])) {
                return false;
            }
        }
        return true;
    }

    getMissingIngredients(ingredients: string[]) : string[] {
        const missingIngredients: string[] = [];
        const formattedWord: string = this.stringifyComplete();
        for (let i = 0; i < formattedWord.length; i++) {
            if (this.letters[i].type === 'given') {
                continue;
            }
            if (
                !ingredients.includes(formattedWord[i])
                && !missingIngredients.includes(formattedWord[i].toLowerCase())
            ) {
                missingIngredients.push(formattedWord[i].toLowerCase());
            }
        }
        return missingIngredients;
    }

    // this will return THE CURRENT STATE
    // EMPTY ENTRIES WILL HOLD A PLACEHOLDER
    // WE STILL CAPITALIZE GIVENS
    stringifyState(placeholder: string = "#"): string {
        // iterate through the module array and concatenate a single string
        let stringy: string = '';
        for (let i = 0; i < this.length; i++) {
            let char: string = this.letters[i].character;

            // if it's an empty entry, override with the placeholder
            if (
                this.letters[i].type === 'entry'
                && this.letters[i].character === ''
            ) {
                char = placeholder;
            }

            // if it's a given, capitalize
            if (this.letters[i].type === 'given') {
                char = char.toUpperCase();
            }

            // concatenate the string
            stringy += char;
        }

        return stringy;
    }


    // THIS WILL RETURN A SOLUTION STATE BASED ON THE SEED
    // it will fill in entry slots and capitalize givens
    stringifyComplete() : string {
        let stringy: string = '';
        for (let i = 0; i < this.length; i++) {
            let char = this.puzzleWord[i];
            // if this position is a given
            if (this.letters[i].type === 'given') {
                // we capitalize
                char = char.toUpperCase();
            }
            // concatenate
            stringy += char;
        }
        return stringy;
    }

    // THIS WILL RETURN A STRING WITH ONLY PLACEHOLDERS AND GIVENS
    stringifySignature(placeholder: string = "#"): string {
        let stringy: string = '';
        for (let i = 0; i < this.length; i++) {
            let char = this.letters[i].type === 'given'
                ? this.puzzleWord[i].toUpperCase()
                : placeholder

            stringy += char;
            }
        return stringy;
    }
}